import type { Patient } from '@dataTypes/PatientInfo.types'

export const PATIENT_REQUIRED_FIELDS: (keyof Patient)[] = [
  'first_name',
  'last_name',
  'middle_name',
  'date_of_birth',
  'gender',
  'marital_status',
  'address',
  'city',
  'state',
  'zip',
  'email',
  'cellphone',
]

const fieldLabel = (field: keyof Patient): string => {
  const text = String(field)
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .toLowerCase()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const formatPatientPhoneNumber = (phone: string): string => {
  let phoneNumber = phone
  if (phone.startsWith('+')) {
    const numberParts = phone.split(' ')
    numberParts.shift()
    phoneNumber = numberParts.join('')
  }

  const cleaned = phoneNumber.replace(/\D/g, '')
  return cleaned.length === 10
    ? `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    : phoneNumber
}

export const validatePhone = (
  value: string | null | undefined,
  label: string,
): string | null => {
  let phone = value?.trim() ?? ''
  if (phone.startsWith('+')) {
    const parts = phone.split(' ')
    parts.shift()
    phone = parts.join('')
  }

  const cleaned = phone.replace(/\D/g, '')
  if (label === 'Mobile' && !phone) return `${label} phone is required`
  if (cleaned.length !== 10) {
    if (label !== 'Mobile' && cleaned.length === 0) return null
    return `${label} phone must be exactly 10 digits`
  }
  return null
}

export const validateRequiredPatientFields = (
  patientData: Patient,
  requiredFields: (keyof Patient)[],
): Record<string, string> => {
  const errors: Record<string, string> = {}
  requiredFields.forEach((field) => {
    if (!patientData[field]) errors[field] = `${fieldLabel(field)} is required`
  })
  return errors
}

export const validatePatientStep = (
  patientData: Patient,
  step: 1 | 2,
): Record<string, string> => {
  const requiredFields: (keyof Patient)[] =
    step === 1
      ? PATIENT_REQUIRED_FIELDS
      : patientData.has_insurance
        ? ['carrier_id']
        : []
  const errors = validateRequiredPatientFields(patientData, requiredFields)

  const mobileError = validatePhone(patientData.cellphone, 'Mobile')
  const homeError = validatePhone(patientData.home_phone, 'Home')
  const workError = validatePhone(patientData.work_phone, 'Work')
  const faxError = validatePhone(patientData.fax_number, 'Fax')
  if (mobileError) errors.mobile = mobileError
  if (homeError) errors.home_phone = homeError
  if (workError) errors.work_phone = workError
  if (faxError) errors.fax_number = faxError
  if (errors.carrier_id) {
    errors.insurance_provider = 'Insurance provider is required'
  }
  return errors
}

export const normalizePatientForSave = (
  patientData: Patient,
  clinicId?: string,
): Patient => ({
  ...patientData,
  clinic_id: patientData.patient_id
    ? patientData.clinic_id
    : clinicId || patientData.clinic_id,
  first_name: patientData.first_name.trim(),
  last_name: patientData.last_name.trim(),
  email: patientData.email.trim(),
  cellphone: patientData.cellphone
    ? formatPatientPhoneNumber(patientData.cellphone.trim())
    : '',
  home_phone: patientData.home_phone
    ? formatPatientPhoneNumber(patientData.home_phone.trim())
    : '',
  work_phone: patientData.work_phone
    ? formatPatientPhoneNumber(patientData.work_phone.trim())
    : '',
  fax_number: patientData.fax_number
    ? formatPatientPhoneNumber(patientData.fax_number.trim())
    : '',
  patient_status: 0,
})

export const validatePatientForDraft = (
  patientData: Patient,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const required: (keyof Patient)[] = [
    'first_name',
    'middle_name',
    'last_name',
    'email',
    'date_of_birth',
    'cellphone',
  ]
  required.forEach((field) => {
    if (!patientData[field]) errors[field] = `${fieldLabel(field)} is required`
  })
  const phoneError = validatePhone(patientData.cellphone, 'Mobile')

  if (phoneError) errors.cellphone = phoneError
  if (patientData.email && !patientData.email.includes('@')) {
    errors.email = 'Email is required'
  }
  return errors
}

export const normalizePatientForDraft = (
  patientData: Patient,
  clinicId?: string,
): Patient => ({
  ...patientData,
  clinic_id: clinicId,
  first_name: patientData.first_name.trim(),
  last_name: patientData.last_name.trim(),
  email: patientData.email.trim(),
  date_of_birth: patientData.date_of_birth,
  cellphone: formatPatientPhoneNumber(patientData.cellphone.trim()),
  gender: patientData.gender || 'Other',
  marital_status: patientData.marital_status || 'Other',
  address: patientData.address || 'Draft',
  city: patientData.city || 'Draft',
  state: patientData.state || 'NA',
  zip: patientData.zip || '00000',
  has_insurance: patientData.has_insurance ?? false,
  patient_status: 34,
})

export const cleanEmptyPatientFields = (
  patientData: Patient,
): Omit<Partial<Patient>, 'id'> => {
  const cleaned: Partial<Patient> = { ...patientData }
  ;(Object.keys(cleaned) as (keyof Patient)[]).forEach((key) => {
    if (cleaned[key] === '') delete cleaned[key]
  })
  return cleaned
}
